// @givmo/board — having a record removed from the minute book.
//
// ══ THE THREE THINGS THE BACKEND CAN REFUSE, AND HOW EACH ONE READS ════════════════════════════
//
//   1. A LEGAL HOLD covers it. Refused outright, in a trigger independent of every other gate.
//   2. IT IS STILL INSIDE ITS RETENTION FLOOR — at least ten calendar years from the date the record
//      is ABOUT. Deterministic arithmetic over the record's own dates, never a judgement, and a
//      minimum rather than an expiry.
//   3. IT IS PART OF A CORRECTION CHAIN. A corrected record and its correction are one another's
//      context: removing only the correction would silently un-correct the record, and removing only
//      the original would leave a correction of nothing. So the chain is the unit, and a
//      single-member request is refused with a 409 naming the chain size and pointing at the
//      operation that can act on it.
//
// All three arrive as `message` on a 409 and are rendered VERBATIM. They are written for this exact
// moment — one names the chain size, one is arithmetic the officer can check against their own dates
// — and the api-client's own guidance is to render the message and branch on `status`, never on text.
//
// ⚠ WHICH OPERATION IS OFFERED IS DECIDED FROM THE RECORD, NOT FROM THE REFUSAL. If the record shows
// a correction pointer, the chain operation is offered up front — expanding SILENTLY is what
// "deletion is never silent" exists to prevent, so the officer is told how many records go and has to
// choose that. If it shows none, the single-record route is used. A reader whose correction pointer
// was BLANKED sees neither, gets the single route, and gets the backend's refusal verbatim if the
// record turns out to be in a chain — which is the correct dead end, because the chain operation
// would refuse them too, on a member they may not read.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '../../board-routing';
import { isGivmoError, type OrgBoardRecordOut } from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, Textarea, toast } from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { isInCorrectionChain } from './records-decode';
import { useBoardOperations } from '../../board-runtime';

export interface DeleteRecordDialogProps {
  orgRef: string;
  record: OrgBoardRecordOut;
  onClose: () => void;
}

export function DeleteRecordDialog({ orgRef, record, onClose }: DeleteRecordDialogProps) {
  const operations = useBoardOperations();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const asChain = isInCorrectionChain(record);

  const remove = useMutation({
    mutationFn: async () => {
      const path = { org_ref: orgRef, record_id: record.id };
      const body = { reason: reason.trim() };
      if (asChain) {
        await operations.officerDeleteBoardRecordChain({ path, body, throwOnError: true });
        return;
      }
      await operations.officerDeleteBoardRecord({ path, body, throwOnError: true });
    },
    onSuccess: async () => {
      toast.success('Removed. The fact that it was removed, and why, is kept.');
      await queryClient.invalidateQueries({ queryKey: ['board-records', orgRef] });
      await queryClient.invalidateQueries({ queryKey: ['board-governing-documents', orgRef] });
      onClose();
      await navigate({ to: '/records' });
    },
    onError: (caught) =>
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t remove that. Please try again.',
      ),
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !remove.isPending && onClose()}>
      <Dialog.Content
        aria-label="Remove this record"
        className="flex flex-col gap-16"
        testId="record-delete-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Remove this record
        </Dialog.Title>

        {asChain ? (
          <p
            className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
            data-testid="record-delete-chain-notice"
          >
            This record and the correction it is kept with go together. A correction on its own
            would leave a correction of nothing, and the corrected record on its own would quietly
            lose its correction — so both are removed, or neither is.
          </p>
        ) : (
          <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            This takes the record out of your minute book. What it said goes; the fact that it was
            removed, when, by whom and why stays.
          </p>
        )}

        <Textarea
          label="Why it is being removed"
          rows={4}
          value={reason}
          maxLength={2000}
          onChange={(event) => setReason(event.target.value)}
          testId="record-delete-reason"
        />
        <FieldNote>
          Required, and kept for good — it outlives the record itself, which is what makes this
          something your board can account for later.
        </FieldNote>

        {error !== null ? <ErrorMessage testId="record-delete-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={remove.isPending}
            onClick={onClose}
            testId="record-delete-cancel"
          >
            Keep it
          </Button>
          <Button
            appearance="saas"
            tone="danger"
            loading={remove.isPending}
            disabled={reason.trim().length === 0 || remove.isPending}
            onClick={() => {
              setError(null);
              remove.mutate();
            }}
            testId="record-delete-confirm"
          >
            {asChain ? 'Remove them' : 'Remove it'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
